import React, { useState, useEffect } from 'react'
import { Link, useSearchParams, useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth.jsx'
import { Button, Input, Card, Alert } from '../components'
import { useT } from '../shared/i18n/LanguageContext.jsx'
import LanguageToggle from '../shared/i18n/LanguageToggle.jsx'

/**
 * The forgotten-password path — translated for the same reason the sign-in page is, plus one more.
 *
 * This is the screen somebody reaches when they are ALREADY stuck. Leaving it in English means a
 * Spanish-speaking foreman who has forgotten a password has no way back into the application that
 * does not go through another person, which is the same dead end the sign-in page's toggle exists to
 * remove. The toggle is repeated here because this page is reachable from a link in an email —
 * nobody has necessarily passed through the sign-in page to get here.
 */
const ResetPassword = () => {
  const t = useT('auth')
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  // F-L3: read the reset token from the URL fragment (#token=…). Unlike the query string, a fragment is
  // never sent to any server (no nginx/ALB access-log entry) and never appears in a Referer header. Fall
  // back to the legacy ?token= so links already in inboxes (30-min TTL) still work across the deploy.
  // Snapshot into state on the FIRST render, before the URL is scrubbed below.
  const [token] = useState(
    () => new URLSearchParams((window.location.hash || '').replace(/^#/, '')).get('token')
          || searchParams.get('token') || ''
  )
  const isResetMode = !!token
  // Strip the token from the address bar + history entry so it can't linger in history or leak via a
  // same-origin Referer fired from this page.
  useEffect(() => {
    if (token) window.history.replaceState({}, document.title, window.location.pathname)
  }, [token])

  const [formData, setFormData] = useState({
    email: '',
    newPassword: '',
    confirmPassword: ''
  })
  const [errors, setErrors] = useState({})
  const [success, setSuccess] = useState(false)

  const { requestPasswordReset, resetPassword, loading, error, clearError } = useAuth()

  // Clear errors when component unmounts
  useEffect(() => {
    return () => clearError()
  }, [clearError])

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
    
    // Clear field error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }))
    }
  }

  const validateRequestForm = () => {
    const newErrors = {}

    if (!formData.email.trim()) {
      newErrors.email = t('emailRequired')
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = t('emailInvalid')
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const validateResetForm = () => {
    const newErrors = {}

    if (!formData.newPassword) {
      newErrors.newPassword = t('newPasswordRequired')
    } else if (formData.newPassword.length < 8) {
      newErrors.newPassword = t('passwordTooShort')
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = t('confirmYourPassword')
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = t('passwordsDoNotMatch')
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleRequestSubmit = async (e) => {
    e.preventDefault()
    
    if (!validateRequestForm()) return

    const result = await requestPasswordReset(formData.email)
    
    if (result.success) {
      setSuccess(true)
    }
  }

  const handleResetSubmit = async (e) => {
    e.preventDefault()
    
    if (!validateResetForm()) return

    const result = await resetPassword(token, formData.newPassword)
    
    if (result.success) {
      setSuccess(true)
      // Redirect to login after a short delay
      setTimeout(() => {
        navigate('/login', { 
          state: { 
            message: t('resetSucceededSignInNow') 
          }
        })
      }, 2000)
    }
  }

  const renderRequestForm = () => (
    <form onSubmit={handleRequestSubmit} className="space-y-6">
      <Input
        label={t('emailAddress')}
        type="email"
        name="email"
        value={formData.email}
        onChange={handleInputChange}
        error={errors.email}
        placeholder={t('resetEmailPlaceholder')}
        required
        autoComplete="email"
        autoFocus
      />

      <Button
        type="submit"
        variant="primary"
        loading={loading}
        disabled={loading}
        className="w-full"
      >
        {loading ? t('sending') : t('sendResetLink')}
      </Button>
    </form>
  )

  const renderResetForm = () => (
    <form onSubmit={handleResetSubmit} className="space-y-6">
      <Input
        label={t('newPassword')}
        type="password"
        name="newPassword"
        value={formData.newPassword}
        onChange={handleInputChange}
        error={errors.newPassword}
        placeholder={t('newPasswordPlaceholder')}
        required
        autoComplete="new-password"
        autoFocus
      />

      <Input
        label={t('confirmNewPassword')}
        type="password"
        name="confirmPassword"
        value={formData.confirmPassword}
        onChange={handleInputChange}
        error={errors.confirmPassword}
        placeholder={t('confirmNewPasswordPlaceholder')}
        required
        autoComplete="new-password"
      />

      <Button
        type="submit"
        variant="primary"
        loading={loading}
        disabled={loading}
        className="w-full"
      >
        {loading ? t('resetting') : t('resetPassword')}
      </Button>
    </form>
  )

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          {/* Repeated from the sign-in page rather than inherited, because this page is reachable
              straight from a link in an email — nobody has necessarily passed through sign-in to get
              here, so the device choice may never have been offered on this browser at all. */}
          <div className="flex justify-end -mb-2">
            <LanguageToggle className="text-sm text-gray-500" />
          </div>
          <h2 className="mt-6 text-3xl font-bold text-gray-900">
            {isResetMode ? t('setNewPasswordTitle') : t('resetTitle')}
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            {isResetMode ? t('setNewPasswordSubtitle') : t('resetSubtitle')}
          </p>
        </div>

        {/* Form */}
        <Card className="mt-8">
          {success && (
            <Alert
              type="success"
              title={isResetMode ? t('resetSucceededTitle') : t('resetLinkSent')}
              message={isResetMode ? t('resetSucceededBody') : t('resetLinkSentBody')}
              className="mb-6"
            />
          )}

          {error && (
            <Alert
              type="error"
              title={t('error')}
              message={error}
              onClose={clearError}
              className="mb-6"
            />
          )}

          {!success && (
            isResetMode ? renderResetForm() : renderRequestForm()
          )}

          {!success && (
            <div className="mt-6 text-center">
              <Link
                to="/login"
                className="bfs-link text-sm"
              >
                ← {t('backToSignIn')}
              </Link>
            </div>
          )}
        </Card>
      </div>
    </div>
  )
}

export default ResetPassword
